import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import safeToast from '../utils/safeToast';
import { X, Send, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { Lead } from '../types';

// Submit lead to internal API instead of launching external email client.
// Returns created lead id or queued indicator. Throws on network/server error (non-2xx excluding 202).
async function submitLead(data: Omit<Lead, 'id' | 'submittedAt'> & { message?: string }): Promise<{ id?: string; queued?: boolean }> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12000); // 12s safety timeout
  try {
    const res = await fetch('/api/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      signal: controller.signal,
    });
    if (res.status === 202) {
      return { queued: true };
    }
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`Lead submit failed (${res.status}) ${detail}`);
    }
    const json = await res.json().catch(() => ({}));
    return { id: json.id || undefined };
  } finally {
    clearTimeout(timeout);
  }
}

interface EnhancedEmailLeadFormProps {
  isOpen: boolean;
  onClose: () => void;
  recipientName: string;
  recipientEmail: string;
  cardOwnerId: string;
  onLeadSubmit?: (lead: Omit<Lead, 'id' | 'submittedAt'>) => void;
  onAnalyticsUpdate?: () => void;
}

export const EnhancedEmailLeadForm: React.FC<EnhancedEmailLeadFormProps> = ({ 
  isOpen, 
  onClose, 
  recipientName, 
  recipientEmail,
  cardOwnerId,
  onLeadSubmit,
  onAnalyticsUpdate
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const effectiveOwnerId = React.useMemo(() => {
    if (cardOwnerId && String(cardOwnerId).trim()) return String(cardOwnerId).trim();
    try {
      const host = window.location.hostname.replace(/^www\./,'');
      const parts = host.split('.');
      if (parts.length >= 3) {
        const sub = parts[0];
        if (sub && sub.toLowerCase() !== 'www') return sub;
      }
      const m = window.location.pathname.match(/\/(?:card|u)\/([^/?#]+)/i);
      if (m && m[1]) return decodeURIComponent(m[1]);
    } catch {}
    return '';
  }, [cardOwnerId]);

  if (!isOpen) {
    // Keep hooks order consistent; render null after computing memoized values
    return null;
  }

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    if (!effectiveOwnerId) {
      newErrors.cardOwnerId = 'Unable to determine recipient';
    }
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Build payload
      const leadData: Omit<Lead, 'id' | 'submittedAt'> = {
        cardOwnerId: effectiveOwnerId,
        cardOwnerEmail: recipientEmail,
        cardOwnerName: recipientName,
        fullName: formData.fullName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        message: formData.message,
        source: 'email_form',
        isRead: false,
        isExistingUser: false,
        tags: []
      };

      // Attempt submit to internal API
      let id: string | undefined; let queued = false;
      try {
        const r = await submitLead(leadData);
        id = r.id; queued = !!r.queued;
      } catch (err: any) {
        // Fallback: queue locally (invoke parent callback) and inform user
        queued = true;
        console.warn('Lead submit failed; queued locally', err?.message || err);
      }

      // Notify parent (always) for optimistic UI / analytics
      if (onLeadSubmit) onLeadSubmit(leadData);
      if (onAnalyticsUpdate) onAnalyticsUpdate();

      safeToast('success', queued
        ? 'Contact saved and queued. It will sync when connectivity resumes.'
        : 'Message Sent Successfuly');

      // Reset & close
      setFormData({ fullName: '', email: '', phoneNumber: '', message: '' });
      setErrors({});
      onClose();
    } catch (error: any) {
      safeToast('error', error?.message ? `Failed to send: ${error.message}` : 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev,
      [name]: value 
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Render the modal in a portal to escape any parent stacking contexts (admin/leads panels)
  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 2147483647 }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 relative max-h-[90vh] overflow-y-auto">
        <button type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Send Message</h3>
          <p className="text-gray-600">Send a message to {recipientName}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Full Name *
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.fullName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your full name"
            />
            {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 inline mr-1" />
              Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your email address"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="w-4 h-4 inline mr-1" />
              Phone Number *
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your phone number"
            />
            {errors.phoneNumber && <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MessageSquare className="w-4 h-4 inline mr-1" />
              Message *
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                errors.message ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your message..."
            />
            {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Message
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};